import React, {useState, useEffect} from 'react'
import {Input, Button} from 'reactstrap'
import {useTranslation} from 'react-i18next'
import compose from 'lodash/flowRight'
import classnames from 'classnames'
import useSmartNavigate from 'hooks/useSmartNavigate'

import {breadcrumbsStore} from 'appBase/TopNav'

import withUriParams from 'hocs/withUriParams'
import withInitialDataLoadWaiting from 'hocs/withInitialDataLoadWaiting'

import CheckBox from 'components/elements/CheckBox'
import PanelModal from 'components/modals/PanelModal'
import NamedBlock from 'components/blocks/NamedBlock'
import ListWithSort from 'components/blocks/ListWithSort'

import entityUtils from 'utils/entityUtils'

import {queryLogs} from '../data/serviceQueries'
import {SERVICE_LOGS_VIEW} from 'constants/constants'


const LogsView = compose(
     withUriParams([['expression', String, {optional: true}]]),
     queryLogs,
     withInitialDataLoadWaiting(['logs'])
)((props) => {
    const {expression, logs, uri} = props;
    const {t} = useTranslation();
    const [currentLog, setCurrentLog] = useState(null);
    const [lastLog, setLastLog] = useState(null);

    const navigate = useSmartNavigate();

    // breadcrumbs
    breadcrumbsStore.register(SERVICE_LOGS_VIEW);
    useEffect(() => {
        breadcrumbsStore.set(SERVICE_LOGS_VIEW, {name: t('appService.logs'), to: `${props.uri}`}, true);
    });

    // registered objectTypes
    const registeredObjectTypes = [
        {field: "catalog", value: t('appService.views.LogsView.objectTypes.catalog')},
        {field: "catalog_entry", value: t('appService.views.LogsView.objectTypes.catalogEntry')},
        {field: "catalog_entry_version", value: t('appService.views.LogsView.objectTypes.catalogEntryVersion')},
        {field: "device", value: t('appService.views.LogsView.objectTypes.device')},
        {field: "event", value: t('appService.views.LogsView.objectTypes.event')},
        {field: "license", value: t('appService.views.LogsView.objectTypes.license')},
        {field: "region", value: t('appService.views.LogsView.objectTypes.region')},
        {field: "scenario", value: t('appService.views.LogsView.objectTypes.scenario')},
        {field: "solution", value: t('appService.views.LogsView.objectTypes.solution')},
        {field: "user", value: t('appService.views.LogsView.objectTypes.user')}
    ];

    // parsing expression, extracting object types
    let objectTypes = [];
    const objectTypesPart = expression?.match(/\[(.+)\]/);
    if (objectTypesPart) {
        objectTypes = objectTypesPart[1].split(",").filter(e => e);
    }

    // ...extracting author ids
    let authorIds = [];
    const authorIdsPart = expression?.match(/\((.+)\)/);
    const [authorIdsString, setAuthorIdsString] = useState(authorIdsPart ? authorIdsPart[1]: "");
    if (authorIdsPart) {
        authorIds = authorIdsPart[1].split(",").filter(e => e);
    }

    // ...extracting object ids
    let objectIds = [];
    const objectIdsPart = expression?.match(/«(.+)»/);
    const [objectIdsString, setObjectIdsString] = useState(objectIdsPart ? objectIdsPart[1]: "");
    if (objectIdsPart) {
        objectIds = objectIdsPart[1].split(",").filter(e => e);
    }

    // ...extracting page
    let page = 0;
    const pagePart = expression?.match(/<(.+)>/);
    if (pagePart) {
        const safeNumber = (val) => {
            const number = Number(val);
            return Number.isNaN(number) ? 0: number;
        };
        page = safeNumber(pagePart[1]);
    }

    const onChangeAndNavigate = (type, checked, value) => {
        let newExpression;
        switch(type) {
            case "objectType":
                // preserving authorIds, dropping object ids and page
                let newObjectTypes;
                if (checked) {
                   newObjectTypes = [...objectTypes, value].join(',');
                } else {
                   newObjectTypes = objectTypes.filter(v => v !== value).join(',');
                }
                newExpression = '[' + newObjectTypes + ']';
                newExpression += '(' + authorIds.join(',') + ')';
                break;
            case "authorIds":
                // preserving objectTypes and objectIds, dropping page
                let newAuthorIds;
                newExpression = "[" + objectTypes.join(',') + "]";
                if (checked) {
                    // adding new authorIds, preserving old authorIds
                    newAuthorIds = [...new Set([...authorIds, ...value.split(',')])];
                } else {
                    //it's possible to remove only one id
                    newAuthorIds = [authorIds.filter(id => id !== value)];
                }
                newExpression += '(' + newAuthorIds + ')';
                newExpression += "«" + objectIds.join(',') + "»";
                break;
            case "objectIds":
                // preserving fixed objectType, preserving authorIds, dropping page
                let newObjectIds;
                if (checked) {
                    newExpression = "[" + value.objectType + "]";
                    newObjectIds = [...new Set([...objectIds, ...value.objectId.split(',')])];
                } else {
                    newExpression = "[" + objectTypes.join(',') + "]";
                    newObjectIds = [objectIds.filter(id => id !== value.objectId)];
                }
                newExpression += '(' + authorIds + ')';
                newExpression += "«" + newObjectIds + "»";
                break;
            case "page":
                // preserving all other values
                newExpression = "[" + objectTypes.join(',') + "]";
                newExpression += "(" + authorIds.join(',') + ")";
                newExpression += "«" + objectIds.join(',') + "»";
                page = page + value;
                newExpression += '<' + page + '>';
                break;
            default:
                throw new Error(`Unknown type ${type}`);
        }
        navigate(`${uri}/${newExpression}`);
    };

    // checkBoxes for object types
    const objectTypesCheckBoxes = registeredObjectTypes.map((t, i) =>
        (<CheckBox checked={objectTypes.includes(t.field)} key={i} label={t.value}
            onChange={(e) => onChangeAndNavigate("objectType", e.nativeEvent.target.checked, t.field)} />));

    // checkBoxes for authors ids
    const authorsCheckBoxes = authorIds.map((id, i) =>
        (<CheckBox checked key={i} label={id}
            onChange={(e) => onChangeAndNavigate('authorIds', false, String(id))} />));

    // checkBoxes for objects ids
    const objectsCheckBoxes = objectIds.map((id, i) =>
        (<CheckBox checked key={i} label={id}
            onChange={(e) => onChangeAndNavigate('objectIds', false, {objectId: String(id)})} />));

    // rows
    const rows = logs.map(log => {
        const parameters = entityUtils.safeJSONParse(log.parameters);
        return {
            fields: [
                log.id,
                log.objectType,
                log.objectId,
                log.message,
                log.authorId,
                parameters.author_email,
                parameters.author_name,
                log.createdAt && log.createdAt.substring(0, 19).replace('T', ' '),
            ],
            actions: {
                "select": () => {
                    const selectedLog = {
                        id: log.id,
                        objectId: log.objectId,
                        authorId: log.authorId,
                        objectType: log.objectType,
                        message: log.message,
                        parameters
                    };
                    setCurrentLog(selectedLog);
                    setLastLog(selectedLog);
                }
            },
            className: (log.level === 2) ?
                            classnames("text-success", (log.id === lastLog?.id) && "border border-success"):
                       (log.level === 3) ?
                            classnames("text-danger", (log.id === lastLog?.id) && "border border-danger"):
                            classnames((log.id === lastLog?.id) && "border border-primary"),
        }
    });

    const currentLogKeys = currentLog ? Object.keys(currentLog.parameters): [];
    const currentLogRows = currentLogKeys.map(key => ({
        fields: [key, currentLog.parameters[key]]
    }));
    currentLogRows.unshift({fields: [t('appService.views.LogsView.authorId'), currentLog?.authorId]});
    currentLogRows.unshift({fields: [t('appService.views.LogsView.objectId'), currentLog?.objectId]});

    const onClose = () => {
        setLastLog(currentLog);
        setCurrentLog(null);
    };
    return (
        <div className="d-flex flex-column h-100 w-100">
            <div className="flex-grow d-flex flex-column scroll-parent">
                <div className="flex-grow d-flex scroll-parent">
                    <div className="flex-grow scroll">

                        <div className="container mt-4 ms-1">
                            <div className="row">
                                <div className="col-3">
                                    <NamedBlock name={t('appService.views.LogsView.objectTypeFilters')}>
                                        {objectTypesCheckBoxes}
                                    </NamedBlock>
                                </div>

                                <div className="col-3">
                                    <NamedBlock name={t('appService.views.LogsView.authorFilters')}>
                                        {authorsCheckBoxes}

                                        <Input type="text" value={authorIdsString} placeholder={t('appService.views.LogsView.authorPlaceholder')}
                                               className="my-2" onChange={(e) => setAuthorIdsString(e.nativeEvent.target.value)} />

                                        <Button color="primary" className="mb-2" onClick={() => onChangeAndNavigate("authorIds", true, authorIdsString)}>
                                            {t('appService.views.LogsView.addAuthor')}
                                        </Button>
                                    </NamedBlock>
                                </div>

                                {objectTypes && objectTypes.length === 1 &&
                                <div className="col-3">
                                    <NamedBlock name={t('appService.views.LogsView.objectFilters')}>
                                        {objectsCheckBoxes}

                                        <Input type="text" value={objectIdsString} placeholder={t('appService.views.LogsView.objectPlaceholder')}
                                               className="my-2" onChange={(e) => setObjectIdsString(e.nativeEvent.target.value)} />

                                        <Button color="primary" className="mb-2" onClick={() => onChangeAndNavigate("objectIds", true, {objectId: objectIdsString, objectType: objectTypes[0]})}>
                                            {t('appService.views.LogsView.addObject')}
                                        </Button>
                                    </NamedBlock>
                                </div>}
                            </div>
                        </div>

                        <h5 className="text-center mt-3">{t('appService.logs')}</h5>

                        <div className="container-fluid">
                            <div className="d-flex justify-content-between mb-3">
                                <Button disabled={!page} color={page ? "primary": "secondary"} className="m-2"
                                        onClick={() => onChangeAndNavigate("page", true, -1)}>
                                    {t('appService.views.LogsView.newer')}
                                </Button>

                                <div className={'ms-auto'}>
                                    <Button color="primary" className="m-2"
                                            onClick={() => onChangeAndNavigate("page", true, 1)}>
                                        {t('appService.views.LogsView.older')}
                                    </Button>
                                </div>
                            </div>

                            <NamedBlock>
                                <ListWithSort headers={[t('appService.views.LogsView.id'),
                                                        t('appService.views.LogsView.objectType'),
                                                        t('appService.views.LogsView.objectId'),
                                                        t('appService.views.LogsView.message'),
                                                        t('appService.views.LogsView.authorId'),
                                                        t('appService.views.LogsView.email'),
                                                        t('appService.views.LogsView.name'),
                                                        t('appService.views.LogsView.createdAt')]}
                                              sizes={["40*", "100*", "40*", "200*", "40*", "190*", "120*",  "100*"]}
                                              rows={rows} />
                            </NamedBlock>
                        </div>

                        {currentLog &&
                        <PanelModal show title={currentLog.message} noPadding scrollable onClose={onClose}>
                           <div>
                               <ListWithSort rows={currentLogRows} sizes={["30*", "70*"]} />
                               <Button color="primary" className="m-2"
                                       onClick={() => onChangeAndNavigate("objectIds", true, {objectId: String(currentLog.objectId), objectType: currentLog.objectType})}>
                                   {t('appService.views.LogsView.allObjectActions')}
                               </Button>
                               <Button color="primary" className="m-2"
                                       onClick={() => onChangeAndNavigate("authorIds", true, String(currentLog.authorId))}>
                                   {t('appService.views.LogsView.allAuthorActions')}
                                </Button>
                            </div>
                        </PanelModal>}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default LogsView;